import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { colors2, LoaderCircleV2, ButtonGhostCompact } from "oolib";
import { UI_BODY_SM, UI_BODY_SEMIBOLD_SM, UI_CAPTION, UI_CAPTION_SEMIBOLD, UI_TAG_DF } from "oolib/dist/v2/components/Typo2";
import { getRoutePathToPublishedContent } from "@okf/ootils";
import { useGetPriorityFindingEvidence } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { TagDetailSidebar, SectionLabel } from "@/_self_managed/SM_Dashboard/LensesTab/comps/TagDetailSidebar";

/**
 * The evidence behind one finding card — what the rule read, how it ranked,
 * the scored sub-topics with their trajectories over the window, the topic's
 * mix per period, every observation under the sub-topics, and the exact
 * facts the narrator was handed. Traceability, step one: nothing here is
 * computed on the client; it is the server's derivation laid open
 * (POST getPriorityFindingEvidence).
 *
 *   finding       the card ({ id, kind, kindLabel, headline, why }) — null closes
 *   period, compareKey   the tab's selection, so the derivation matches
 *   zoneStyles    the tab's zone colours (text per zone key)
 *   onClose
 */

const Block = styled.div`
  margin-bottom: 22px;
`;

const KV = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 40%) 1fr;
  gap: 4px 12px;
  align-items: baseline;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    text-align: left;
    padding: 4px 6px;
    border-bottom: 1px solid ${colors2.grey10};
    vertical-align: top;
    white-space: nowrap;
  }
  th {
    color: ${colors2.grey50};
    font-weight: 500;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  td.num,
  th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  tr.now td {
    background: ${colors2.grey5};
  }
`;

const Obs = styled.div`
  padding: 10px 12px;
  border: 1px solid ${colors2.grey10};
  border-radius: 8px;
  background: ${colors2.white};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Pre = styled.pre`
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${colors2.grey5};
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 480px;
  overflow: auto;
`;

const fmt = (v, digits = 2) => (typeof v === "number" ? (Number.isInteger(v) ? String(v) : v.toFixed(digits)) : v == null ? "—" : String(v));
const pct = (n, total) => (total ? `${((100 * n) / total).toFixed(1)}%` : "—");
const dateText = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "no date");

// Nested numbers → "a.b.c: value" rows. Arrays print as a list.
const flatten = (obj, prefix = "", out = []) => {
  if (obj == null) return out;
  if (typeof obj !== "object") return [...out, [prefix, obj]];
  if (Array.isArray(obj)) return [...out, [prefix, obj.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))).join(", ")]];
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) flatten(v, key, out);
    else out.push([key, Array.isArray(v) ? v.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))).join(", ") : v]);
  }
  return out;
};

const Rows = ({ rows }) => (
  <KV>
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <UI_CAPTION style={{ color: colors2.grey50 }}>{k}</UI_CAPTION>
        <UI_CAPTION style={{ color: colors2.grey80, wordBreak: "break-word" }}>{fmt(v)}</UI_CAPTION>
      </React.Fragment>
    ))}
  </KV>
);

const Zone = ({ zone, zones, zoneStyles }) => {
  if (!zone) return <span style={{ color: colors2.grey40 }}>—</span>;
  const label = zones?.[zone] || zone;
  const color = zoneStyles?.[zone]?.text || colors2.grey60;
  return <span style={{ color, fontWeight: 600 }}>{label}</span>;
};

const momentumRows = (m, period) => {
  if (!m) return [];
  if (!m.available) return [["available", "no"], ["reason", m.reason || "—"], ["observations this period", m.nNow], ["corpus this period", m.totalNow]];
  const rows = [
    ["mode", m.mode === "history" ? `history (own normal over ${m.history?.length ?? "?"} periods)` : `delta (vs ${period?.prevLabel || "previous period"})`],
    ["band", m.band],
    ["index (bars cleared)", m.index],
    ["share now", `${fmt(m.shareNow)}% (${m.nNow} of ${m.totalNow})`],
  ];
  if (m.mode === "history") rows.push(["usual share (EWMA)", `${fmt(m.baseline)}%`], ["usual swing (σ, floored)", `${fmt(m.sigma)} / ${fmt(m.sigmaEff)}`], ["velocity (σ from normal)", m.velocity]);
  else rows.push(["share then", m.prev ? `${fmt(m.prev.share)}% (${m.prev.n} of ${m.prev.total})` : "—"], ["relative move", `${fmt((m.relDelta || 0) * 100, 0)}%`], ["luck swing × 2 / floor", `${fmt(m.noise * 200, 0)}% / 30% → bar ${fmt(m.threshold * 100, 0)}%`]);
  rows.push(["confidence", m.confidence], ["momentum (move × confidence)", m.momentum]);
  return rows;
};

const Subject = ({ s, ev, zoneStyles, SELF_MANAGED_BASE_CONFIGS }) => {
  const [showAll, setShowAll] = useState(false);
  const zones = ev.vocabulary?.zones;
  const totals = Object.fromEntries((ev.periods || []).map((p) => [p.key, p.total]));
  const obs = s.observations || { available: false, list: [], total: 0 };
  const list = showAll ? obs.list : obs.list.slice(0, 20);
  const sev = s.severity || {};
  return (
    <Block>
      <SectionLabel>Sub-topic</SectionLabel>
      <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{s.label}</UI_BODY_SEMIBOLD_SM>
      {s.description && <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginTop: 2 }}>{s.description}</UI_CAPTION>}
      <UI_CAPTION style={{ color: colors2.grey50, display: "block", marginTop: 4 }}>
        {[s.topic && `in ${s.topic}`, s.signal?.display, s.categoryDisplay, s.datasets?.length ? s.datasets.join(", ") : null].filter(Boolean).join(" · ")}
        {" · "}
        <Zone zone={s.zone} zones={zones} zoneStyles={zoneStyles} />
      </UI_CAPTION>

      <div style={{ marginTop: 12 }}>
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey70, display: "block", marginBottom: 4 }}>Severity {fmt(sev.value)}</UI_CAPTION_SEMIBOLD>
        <Rows
          rows={[
            ["formula", `${fmt(sev.weight)} weight × ${fmt(sev.reach)} reach × ${fmt(sev.voice)} voice`],
            ["weight source", sev.weightSource],
            ["conversations (docs)", sev.docs],
            ["voice", sev.classified ? `${sev.customer} of ${sev.classified} marked observations are customer-side` : "no observations marked"],
          ]}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey70, display: "block", marginBottom: 4 }}>Momentum</UI_CAPTION_SEMIBOLD>
        <Rows rows={momentumRows(s.momentum, ev.period)} />
      </div>

      <div style={{ marginTop: 12 }}>
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey70, display: "block", marginBottom: 4 }}>Trajectory over the window</UI_CAPTION_SEMIBOLD>
        <Table>
          <thead>
            <tr>
              <th>period</th>
              <th className="num">n</th>
              <th className="num">corpus</th>
              <th className="num">share</th>
              <th>zone</th>
              <th className="num">index</th>
              <th>band</th>
              <th className="num">conf.</th>
            </tr>
          </thead>
          <tbody>
            {(s.trajectory || []).map((p) => (
              <tr key={p.key} className={p.key === ev.period.nowKey ? "now" : ""}>
                <td>{p.label}</td>
                <td className="num">{p.n}</td>
                <td className="num">{totals[p.key] ?? "—"}</td>
                <td className="num">{pct(p.n, totals[p.key])}</td>
                <td>
                  <Zone zone={p.zone} zones={zones} zoneStyles={zoneStyles} />
                </td>
                <td className="num">{p.available ? fmt(p.index) : "—"}</td>
                <td>{p.band || "—"}</td>
                <td className="num">{fmt(p.confidence)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div style={{ marginTop: 12 }}>
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey70, display: "block", marginBottom: 6 }}>
          Observations ({obs.total}{obs.available && obs.list.length < obs.total ? `, first ${obs.list.length}` : ""})
        </UI_CAPTION_SEMIBOLD>
        {!obs.available && <UI_CAPTION style={{ color: colors2.grey50 }}>Observations are not carried on this path (scored from aggregates).</UI_CAPTION>}
        {obs.available && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {list.map((o) => {
              const href =
                o.dataset && o.documentId && SELF_MANAGED_BASE_CONFIGS
                  ? getRoutePathToPublishedContent({ contentType: o.dataset, contentId: o.documentId, SELF_MANAGED_BASE_CONFIGS, scrollIntoViewElemId: o.annoKey || undefined })
                  : null;
              return (
                <Obs key={o.id || `${o.documentId}-${o.annoKey}`}>
                  <UI_CAPTION style={{ color: colors2.grey50 }}>
                    {[dateText(o.date), o.periodKey && o.periodKey === ev.period.nowKey ? "this period" : o.periodKey, o.datasetTitle, o.signal, o.customerVoice === true ? "customer" : o.customerVoice === false ? "product-side" : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </UI_CAPTION>
                  {o.title && <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{o.title}</UI_BODY_SEMIBOLD_SM>}
                  {o.desc && <UI_BODY_SM style={{ color: colors2.grey70 }}>{o.desc}</UI_BODY_SM>}
                  <UI_CAPTION style={{ color: colors2.grey50 }}>
                    doc {o.documentId || "—"}
                    {href && (
                      <>
                        {" · "}
                        <a href={href} target="_blank" rel="noreferrer" style={{ color: colors2.grey70 }}>
                          open
                        </a>
                      </>
                    )}
                  </UI_CAPTION>
                </Obs>
              );
            })}
            {obs.list.length > 20 && (
              <ButtonGhostCompact S value={showAll ? "Show fewer" : `Show all ${obs.list.length}`} onClick={() => setShowAll((v) => !v)} />
            )}
          </div>
        )}
      </div>
    </Block>
  );
};

const TopicMix = ({ topic, ev }) => {
  const categories = useMemo(() => [...new Set((topic.mixByPeriod || []).flatMap((p) => Object.keys(p.counts || {})))], [topic]);
  if (!topic.mixByPeriod?.length) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey70, display: "block", marginBottom: 4 }}>Signal-category mix per period</UI_CAPTION_SEMIBOLD>
      <Table>
        <thead>
          <tr>
            <th>period</th>
            <th className="num">total</th>
            {categories.map((c) => (
              <th key={c} className="num">
                {c}
              </th>
            ))}
            <th>leading</th>
          </tr>
        </thead>
        <tbody>
          {topic.mixByPeriod.map((p) => (
            <tr key={p.key} className={p.key === ev.period.nowKey ? "now" : ""}>
              <td>{p.label}</td>
              <td className="num">{p.total}</td>
              {categories.map((c) => (
                <td key={c} className="num">
                  {p.counts?.[c] ?? 0}
                </td>
              ))}
              <td>{p.leading || "—"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export const FindingEvidencePanel = ({ finding, period, compareKey, zoneStyles, onClose }) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const SELF_MANAGED_BASE_CONFIGS = platformConfigs?.SELF_MANAGED_BASE_CONFIGS;
  const { data, isLoading, error } = useGetPriorityFindingEvidence({ id: finding?.id, period, compareKey, enabled: !!finding });
  const [showFacts, setShowFacts] = useState(false);
  const ev = data?.evidence || null;
  const accent = colors2.grey80;

  return (
    <TagDetailSidebar
      isOpen={!!finding}
      onClose={onClose}
      width={560}
      primaryColor={accent}
      overrideHeaderTitle={<UI_TAG_DF style={{ color: accent }}>{finding?.kindLabel || "FINDING"} · evidence</UI_TAG_DF>}
      title={finding?.headline || ""}
      description={finding?.why}
    >
      {isLoading && <LoaderCircleV2 />}
      {error && <UI_BODY_SM style={{ color: colors2.grey60 }}>Couldn’t load the evidence.</UI_BODY_SM>}
      {data && !data.found && !isLoading && (
        <UI_BODY_SM style={{ color: colors2.grey60 }}>This finding is no longer among the period’s findings — the data has moved on. Reload the tab.</UI_BODY_SM>
      )}
      {ev && (
        <>
          <Block>
            <SectionLabel>The rule</SectionLabel>
            <UI_BODY_SM style={{ color: colors2.grey80 }}>{ev.rule?.what || ev.kind}</UI_BODY_SM>
            {ev.rule?.looksBack && <UI_CAPTION style={{ color: colors2.grey50, display: "block", marginTop: 2 }}>Looks back: {ev.rule.looksBack}</UI_CAPTION>}
            <div style={{ marginTop: 10 }}>
              <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey70, display: "block", marginBottom: 2 }}>Computed wording (before the model)</UI_CAPTION_SEMIBOLD>
              <UI_BODY_SM style={{ color: colors2.grey80 }}>{ev.claim}</UI_BODY_SM>
              <UI_CAPTION style={{ color: colors2.grey60, display: "block" }}>{ev.why}</UI_CAPTION>
            </div>
          </Block>

          <Block>
            <SectionLabel>How it ranked</SectionLabel>
            <Rows
              rows={[
                ["rank", `${fmt(ev.rank.value, 3)} = ${fmt(ev.rank.stakes)} stakes × ${fmt(ev.rank.magnitude)} magnitude × ${fmt(ev.rank.confidence)} confidence`],
                ["stakes", `${fmt(ev.rank.stakes)} (severity ÷ ceiling, capped at 1)`],
                ["magnitude", `${fmt(ev.rank.magnitude)} (how big the move or share was)`],
                ["confidence", `${fmt(ev.rank.confidence)} (evidence behind it)`],
              ]}
            />
          </Block>

          <Block>
            <SectionLabel>The numbers the rule read</SectionLabel>
            <Rows rows={flatten(ev.numbers)} />
          </Block>

          <Block>
            <SectionLabel>The period</SectionLabel>
            <Rows
              rows={[
                ["period", `${ev.period.nowLabel} (${ev.period.nowKey}, by ${ev.period.grain})`],
                ["observations in it", ev.period.totalNow],
                ["momentum mode", ev.period.mode === "history" ? `history — each sub-topic against its own normal over ${ev.period.historyPeriods} earlier periods` : `delta — compared with ${ev.period.prevLabel || "nothing (no comparison period)"}`],
                ["comparison key", ev.period.compareKey || "—"],
                ["momentum available", ev.period.momentumAvailable ? "yes" : `no — ${ev.period.reason || ""}`],
              ]}
            />
            <div style={{ marginTop: 10 }}>
              <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey70, display: "block", marginBottom: 4 }}>The window the trajectories span</UI_CAPTION_SEMIBOLD>
              <Table>
                <thead>
                  <tr>
                    <th>period</th>
                    <th className="num">corpus observations</th>
                  </tr>
                </thead>
                <tbody>
                  {ev.periods.map((p) => (
                    <tr key={p.key} className={p.key === ev.period.nowKey ? "now" : ""}>
                      <td>{p.label}</td>
                      <td className="num">{p.total}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Block>

          {ev.topic && (
            <Block>
              <SectionLabel>Topic</SectionLabel>
              <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{ev.topic.label}</UI_BODY_SEMIBOLD_SM>
              {ev.topic.description && <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginTop: 2 }}>{ev.topic.description}</UI_CAPTION>}
              {ev.topic.scored && (
                <UI_CAPTION style={{ color: colors2.grey50, display: "block", marginTop: 4 }}>
                  {ev.topic.rowCount} sub-topics · {ev.topic.scored.n} observations · severity {fmt(ev.topic.scored.severity?.value)}
                  {ev.topic.scored.severity?.worstFinding ? ` (worst: ${ev.topic.scored.severity.worstFinding})` : ""} ·{" "}
                  <Zone zone={ev.topic.scored.zone} zones={ev.vocabulary?.zones} zoneStyles={zoneStyles} />
                </UI_CAPTION>
              )}
              <TopicMix topic={ev.topic} ev={ev} />
            </Block>
          )}

          {ev.subjects.map((s) => (
            <Subject key={s.nodeId} s={s} ev={ev} zoneStyles={zoneStyles} SELF_MANAGED_BASE_CONFIGS={SELF_MANAGED_BASE_CONFIGS} />
          ))}

          <Block>
            <SectionLabel>Facts handed to the narrator</SectionLabel>
            <ButtonGhostCompact S value={showFacts ? "Hide" : "Show the JSON"} onClick={() => setShowFacts((v) => !v)} />
            {showFacts && <Pre style={{ marginTop: 8 }}>{JSON.stringify(ev.narratorFacts, null, 2)}</Pre>}
          </Block>
        </>
      )}
    </TagDetailSidebar>
  );
};
